/**
 * Central Zustand store for the editor V2 rewrite.
 *
 * Holds all editor project/content/selection/ui state and the pure-ish
 * mutation actions ported from the `VideoEditor.tsx` monolith. Structural
 * clip mutations (add/remove/trim/split/reorder/duplicate) always run
 * `recalcClipStarts` afterwards so `start` stays a consistent 0-based
 * cumulative sum of durations.
 */
import { create } from 'zustand';
import { DEFAULT_MODEL_ID } from '@/lib/editor/agent/models';
import { shotToWavespeedRequest, wavespeedModelLabel, type GenShot } from '@/lib/editor/agent/generation';
import {
  detectVideoAspectRatio,
  makeClip,
  makeId,
  persistableAssets,
  persistableClips,
  persistableLayers,
  recalcClipStarts,
  safeClipDuration,
  splitClipAt,
  type EditorMedia,
} from '@/lib/editor/editorClips';
import {
  clampTransitionDuration,
  effectiveTimelineDuration,
  effectiveTransitionDuration,
  renderStartTimes,
  TRANSITION_TYPES,
} from '@/lib/editor/transitions';
import { VIDEO_FILTERS } from '@/lib/editor/filters';
import { cleanGenProposals } from '@/lib/editor/render-contract';
import { getEffectPreset } from '@/lib/editor/effects';
import {
  clipFit,
  clipOpacity,
  clipPlaybackRate,
  clipPositionX,
  clipPositionY,
  clipRotation,
  clipScale,
} from '@/lib/editor/clipProperties';
import { presetToLayerPatch, type TextPreset } from '@/lib/editor/textPresets';
import type { OverlayTemplate } from '@/lib/editor/overlayTemplates';
import { cleanTrackSettings, defaultTrackSettings } from '@/lib/editor/trackSettings';
import { cleanAudioTracks } from '@/lib/editor/render-contract';
import { addClipByTimelineMode, type TimelineInsertMode } from '@/lib/editor/timelineEdit';
import { frameDuration, snapTimeToFrame } from '@/lib/editor/frameGrid';
import { cleanLayerKeyframes, layerVisualAt, layerVisualSnapshot } from '@/lib/editor/layerKeyframes';
import type { EditorLeftMode } from '@/components/studio/editor/EditorIconRail';
import type {
  EditorAspectRatio,
  EditorAudioTrack,
  EditorOverlayLayer,
  EditorOverlayLayerType,
  EditorProject,
  EditorProjectSavePayload,
  EditorStickerAsset,
  EditorTimelineClip,
  EditorTransition,
  EditorTrackControlState,
  EditorTrackKind,
  EditorTrackSettings,
  GenProposal,
  GenShotJob,
} from '@/types/editor';

const VALID_FILTER_IDS = new Set(VIDEO_FILTERS.map((preset) => preset.id));

export type ToolId =
  | 'media'
  | 'text'
  | 'stickers'
  | 'effects'
  | 'audio'
  | 'transitions'
  | 'captions'
  | 'filters'
  | 'templates';

export type { EditorMedia };

// Верхние вкладки-категории ↔ режим левой панели (иконочный рельс V2).
// captions/filters/templates не имеют иконки в рельсе — уводим только с чата.
const LEFT_MODE_BY_TOOL: Partial<Record<ToolId, EditorLeftMode>> = {
  media: 'media',
  text: 'text',
  stickers: 'stickers',
  audio: 'audio',
  effects: 'effects',
  transitions: 'transitions',
};

function getToolForLayer(layer: Pick<EditorOverlayLayer, 'type' | 'role'>): ToolId {
  if (layer.role === 'caption') return 'captions';
  if (layer.type === 'text') return 'text';
  if (layer.type === 'effect') return 'effects';
  return 'stickers';
}

function clampTime(value: number, duration: number) {
  return Math.min(Math.max(value, 0), Math.max(duration, 0));
}

// `useAuthToken().authedFetch` (the real caller from `EditorChatSidebar`) has
// this narrower signature — `url: string`, not DOM `fetch`'s
// `RequestInfo | URL` — since it only ever forwards a string path. `typeof
// fetch` is contravariantly assignable to this (string is a valid
// `RequestInfo | URL`), so callers may still pass the real `fetch` or a
// `typeof fetch`-typed mock (see `editorStore.test.ts`).
type AuthedFetch = (url: string, init?: RequestInit) => Promise<Response>;

type GenJobUpdater = (patch: Partial<GenShotJob>) => void;

/** Submit (or safely retry) one not-yet-charged generation shot. */
async function submitGenerationShot(
  job: GenShotJob,
  proposal: Pick<GenProposal, 'referenceUrl'>,
  authedFetch: AuthedFetch,
  update: GenJobUpdater
): Promise<void> {
  try {
    const { model, params } = shotToWavespeedRequest(job.shot, proposal.referenceUrl);

    update({ state: 'submitting', error: undefined, jobId: undefined, src: undefined });

    if (job.shot.useReferenceImage && proposal.referenceUrl) {
      const refRes = await fetch(proposal.referenceUrl);
      const blob = await refRes.blob();
      const form = new FormData();
      form.append('file', blob, 'reference.png');
      const uploadRes = await authedFetch('/api/ai/wavespeed/upload', {
        method: 'POST',
        body: form,
      });
      const uploadJson = await uploadRes.json();
      const uploadedUrl = uploadJson?.data?.url;
      if (uploadRes.ok && uploadJson?.success && uploadedUrl) {
        if (job.shot.kind === 'video') params.image = uploadedUrl;
        else params.images = [uploadedUrl];
      } else {
        update({
          state: 'error',
          error: uploadJson?.error || `Reference upload failed (HTTP ${uploadRes.status})`,
        });
        return;
      }
    }

    const submitRes = await authedFetch('/api/ai/wavespeed/submit', {
      method: 'POST',
      body: JSON.stringify({ model, prompt: job.shot.prompt, params }),
    });
    const submitJson = await submitRes.json();

    if (!submitRes.ok || !submitJson?.success) {
      update({
        state: 'error',
        error: submitJson?.error || `Submit failed (HTTP ${submitRes.status})`,
      });
      return;
    }

    update({
      state: 'running',
      jobId: submitJson.data.jobId,
      costUsd: submitJson.data.amountCharged,
    });
  } catch (error) {
    update({
      state: 'error',
      error: error instanceof Error ? error.message : 'Submit failed',
    });
  }
}

/**
 * The undoable "document" — the subset of `EditorState` that undo/redo
 * operates on. Deliberately excludes ephemeral UI/selection/playback state
 * (`selectedLayerId`, `currentTime`, `isPlaying`, `activeTool`, generation
 * state, etc.) — see the `history` capture wiring below `create()`.
 */
export interface DocSnapshot {
  clips: EditorTimelineClip[];
  layers: EditorOverlayLayer[];
  assets: EditorStickerAsset[];
  audioTrack: EditorAudioTrack | null;
  audioTracks: EditorAudioTrack[];
  aspectRatio: EditorAspectRatio;
  crossfadeDuration: number;
  filterId: string;
  trackSettings: EditorTrackSettings;
}

function snapshotDoc(state: DocSnapshot): DocSnapshot {
  return {
    clips: state.clips,
    layers: state.layers,
    assets: state.assets,
    audioTrack: state.audioTrack,
    audioTracks: state.audioTracks,
    aspectRatio: state.aspectRatio,
    crossfadeDuration: state.crossfadeDuration,
    filterId: state.filterId,
    trackSettings: state.trackSettings,
  };
}

function defaultLayerRange(state: EditorState, length = 2) {
  const duration = selectDuration(state);
  if (duration <= 0) return { start: 0, end: length };
  const start = clampTime(Math.min(state.currentTime, Math.max(duration - 0.5, 0)), duration);
  const end = Math.min(duration, start + length);
  return { start, end: Math.max(start + 0.5, end) };
}

export interface EditorState {
  // project
  projectId: string | null;
  title: string;
  aspectRatio: EditorAspectRatio;
  aspectTouched: boolean;
  crossfadeDuration: number;
  /** Global color-grade preset id, applied to preview and export. */
  filterId: string;
  modelId: string;
  // content
  clips: EditorTimelineClip[];
  layers: EditorOverlayLayer[];
  assets: EditorStickerAsset[];
  audioTrack: EditorAudioTrack | null;
  audioTracks: EditorAudioTrack[];
  trackSettings: EditorTrackSettings;
  // media library
  library: EditorMedia[];
  // selection & playback
  selectedClipId: string | null;
  selectedLayerId: string | null;
  selectedAudioTrackId: string | null;
  currentTime: number;
  isPlaying: boolean;
  // ui
  leftMode: EditorLeftMode;
  activeTool: ToolId;
  timelineInsertMode: TimelineInsertMode;
  // lifecycle
  draftLoaded: boolean;
  saving: boolean;
  renderJobId: string | null;
  // Canny → Supercomputer generation: proposals awaiting allow/decline +
  // in-flight jobs, keyed by the agent tool-call that spawned them. Lives
  // here (not in the chat sidebar) so it survives `leftMode` switches —
  // the sidebar unmounts, the store doesn't. See `useGenerationPoller`.
  genProposals: GenProposal[];
  // The reference image URL (if any) attached to the most recently SENT
  // chat message — captured by `useEditorAgent.sendMessage` so that when a
  // `proposeGeneration` tool call streams back later, `EditorChatSidebar`
  // can pass it into `createGenProposal` as the shot reference. The
  // composer clears its own attachment state right after send, so this is
  // the only place that value survives past the request that carried it.
  lastBriefReferenceUrl: string | null;
  // undo/redo — coalesced snapshots of the `DocSnapshot` slices only (see
  // `commitHistory`/the `subscribe` wiring below `create()` for how `past`
  // gets populated and `future` invalidated).
  history: { past: DocSnapshot[]; future: DocSnapshot[] };
}

export interface EditorActions {
  hydrate(project: EditorProject): void;
  reset(): void;
  // undo/redo
  undo(): void;
  redo(): void;
  // clips
  addClip(media: EditorMedia): void;
  removeClip(clipId: string): void;
  trimClipIn(clipId: string, deltaSeconds: number): void;
  trimClipOut(clipId: string, deltaSeconds: number): void;
  splitClipAtPlayhead(): void;
  reorderClips(fromIndex: number, toIndex: number): void;
  duplicateClip(clipId: string): void;
  updateClip(clipId: string, patch: Partial<EditorTimelineClip>): void;
  selectClip(clipId: string | null): void;
  setTransition(clipId: string, transition: EditorTransition): void;
  removeTransition(clipId: string): void;
  // layers
  addTextLayer(): void;
  addCaptionLayer(): void;
  addPresetLayer(preset: TextPreset): void;
  applyOverlayTemplate(template: OverlayTemplate): void;
  addStickerLayer(sticker: EditorStickerAsset): void;
  addAsset(asset: EditorStickerAsset): void;
  addEffectLayer(effectSrc: string, label: string): void;
  updateLayer(layerId: string, patch: Partial<EditorOverlayLayer>): void;
  addLayerKeyframe(layerId: string, time?: number): void;
  removeLayerKeyframe(layerId: string, keyframeId: string): void;
  removeLayer(layerId: string): void;
  selectLayer(layerId: string | null): void;
  // audio / format
  setAudioTrack(track: EditorAudioTrack | null): void;
  addAudioTrack(track: EditorAudioTrack): void;
  updateAudioTrack(trackId: string, patch: Partial<EditorAudioTrack>): void;
  removeAudioTrack(trackId: string): void;
  selectAudioTrack(trackId: string | null): void;
  setTrackSettings(kind: EditorTrackKind, patch: Partial<EditorTrackControlState>): void;
  setAspectRatio(ar: EditorAspectRatio, explicit?: boolean): void;
  autoMatchAspectFromClip(src: string): Promise<void>;
  setCrossfade(seconds: number): void;
  setFilter(id: string): void;
  setModelId(id: string): void;
  // library / ui / playback
  setLibrary(items: EditorMedia[]): void;
  setLeftMode(mode: EditorLeftMode): void;
  setActiveTool(tool: ToolId): void;
  setTimelineInsertMode(mode: TimelineInsertMode): void;
  setCurrentTime(t: number): void;
  setIsPlaying(p: boolean): void;
  // persistence
  toSavePayload(): EditorProjectSavePayload;
  setProjectId(id: string): void;
  setSaving(b: boolean): void;
  setRenderJobId(id: string | null): void;
  // generation (Canny → Supercomputer)
  setLastBriefReferenceUrl(url: string | null): void;
  createGenProposal(
    toolCallId: string,
    summary: string,
    shots: GenShot[],
    referenceUrl: string | null,
    authedFetch: AuthedFetch
  ): Promise<void>;
  allowGenProposal(toolCallId: string, authedFetch: AuthedFetch): void;
  retryGenJob(toolCallId: string, localId: string, authedFetch: AuthedFetch): void;
  declineGenProposal(toolCallId: string): void;
  updateGenJob(toolCallId: string, localId: string, patch: Partial<GenShotJob>): void;
}

const initialState: EditorState = {
  projectId: null,
  title: '',
  aspectRatio: '9:16',
  aspectTouched: false,
  crossfadeDuration: 0.3,
  filterId: 'none',
  modelId: DEFAULT_MODEL_ID,
  clips: [],
  layers: [],
  assets: [],
  audioTrack: null,
  audioTracks: [],
  trackSettings: defaultTrackSettings(),
  library: [],
  selectedClipId: null,
  selectedLayerId: null,
  selectedAudioTrackId: null,
  currentTime: 0,
  isPlaying: false,
  leftMode: 'chat',
  activeTool: 'media',
  timelineInsertMode: 'append',
  draftLoaded: false,
  saving: false,
  renderJobId: null,
  genProposals: [],
  lastBriefReferenceUrl: null,
  history: { past: [], future: [] },
};

const HISTORY_LIMIT = 50;
const HISTORY_COALESCE_MS = 450;

// Module-level (not store state) bookkeeping for the coalesced undo/redo
// capture. `lastCommittedDoc` is the doc snapshot as of the last committed
// history entry (i.e. what the top of `past` would become on the *next*
// commit); `historyDebounceTimer` coalesces a burst of edits into one commit;
// `isTimeTraveling` is set around undo/redo's own `set()` so it doesn't
// re-capture itself as a new history entry. See the `subscribe` wiring and
// `commitHistory` below `create()`.
let lastCommittedDoc: DocSnapshot = snapshotDoc(initialState);
let historyDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let isTimeTraveling = false;

/** Cancels any pending coalesced capture and re-baselines `lastCommittedDoc`
 * to `doc` — called on `reset`/`hydrate` (new document loaded: nothing about
 * the previous project should become an undo step for this one) and after
 * undo/redo (the restored doc IS now the baseline). */
function resetHistoryTracking(doc: DocSnapshot) {
  if (historyDebounceTimer) {
    clearTimeout(historyDebounceTimer);
    historyDebounceTimer = null;
  }
  lastCommittedDoc = doc;
}

export const useEditorStore = create<EditorState & EditorActions>()((set, get) => ({
  ...initialState,

  hydrate(project) {
    const legacyFilterId = project.filterId && VALID_FILTER_IDS.has(project.filterId) ? project.filterId : 'none';
    // Legacy global color grade applied to the WHOLE video → migrate onto every
    // clip that has no per-clip filter of its own (not just clip 0, else clips
    // 2..N of an old multi-clip project silently lose the grade on load).
    const clips = recalcClipStarts(persistableClips(project.clips ?? [])).map((clip) =>
      (!clip.filterId || clip.filterId === 'none') && legacyFilterId !== 'none'
        ? { ...clip, filterId: legacyFilterId }
        : clip
    );
    const nextLayers = persistableLayers(project.layers ?? []);
    const nextAssets = persistableAssets(project.assets ?? []);
    const nextAudioTracks = cleanAudioTracks(project.audioTracks, project.audioTrack);
    const nextAudioTrack = nextAudioTracks[0] ?? null;
    const nextCrossfade = typeof project.crossfadeDuration === 'number' ? project.crossfadeDuration : 0.3;
    const nextFilterId = 'none';
    const nextAspectRatio = project.aspectRatio ?? '9:16';
    const nextTrackSettings = cleanTrackSettings(project.trackSettings);
    const nextGenProposals = cleanGenProposals(project.genProposals);
    set({
      projectId: project.id,
      title: project.title,
      clips,
      layers: nextLayers,
      assets: nextAssets,
      audioTrack: nextAudioTrack,
      audioTracks: nextAudioTracks,
      trackSettings: nextTrackSettings,
      genProposals: nextGenProposals,
      crossfadeDuration: nextCrossfade,
      filterId: nextFilterId,
      modelId: project.modelId ?? DEFAULT_MODEL_ID,
      aspectRatio: nextAspectRatio,
      aspectTouched: true,
      selectedClipId: clips[0]?.id ?? null,
      selectedLayerId: null,
      selectedAudioTrackId: nextAudioTrack?.id ?? null,
      currentTime: 0,
      draftLoaded: true,
      history: { past: [], future: [] },
    });
    // A newly-loaded project's doc must never let a stray debounce commit
    // (armed by the `set` above, since these are all doc slices) push the
    // PREVIOUS project's state into this project's fresh `history.past`.
    resetHistoryTracking({
      clips,
      layers: nextLayers,
      assets: nextAssets,
      audioTrack: nextAudioTrack,
      audioTracks: nextAudioTracks,
      aspectRatio: nextAspectRatio,
      crossfadeDuration: nextCrossfade,
      filterId: nextFilterId,
      trackSettings: nextTrackSettings,
    });
  },

  reset() {
    resetHistoryTracking(snapshotDoc(initialState));
    set({ ...initialState, history: { past: [], future: [] } });
  },

  undo() {
    const state = get();
    const { past, future } = state.history;
    if (past.length === 0) return;
    isTimeTraveling = true;
    try {
      const previous = past[past.length - 1];
      const current = snapshotDoc(state);
      const restoredLayerIds = new Set(previous.layers.map((l) => l.id));
      const restoredClipIds = new Set(previous.clips.map((c) => c.id));
      set({
        ...previous,
        history: { past: past.slice(0, -1), future: [current, ...future] },
        selectedLayerId:
          state.selectedLayerId && restoredLayerIds.has(state.selectedLayerId) ? state.selectedLayerId : null,
        selectedClipId:
          state.selectedClipId && restoredClipIds.has(state.selectedClipId) ? state.selectedClipId : null,
      });
      resetHistoryTracking(previous);
    } finally {
      isTimeTraveling = false;
    }
  },

  redo() {
    const state = get();
    const { past, future } = state.history;
    if (future.length === 0) return;
    isTimeTraveling = true;
    try {
      const next = future[0];
      const current = snapshotDoc(state);
      const restoredLayerIds = new Set(next.layers.map((l) => l.id));
      const restoredClipIds = new Set(next.clips.map((c) => c.id));
      set({
        ...next,
        history: { past: [...past, current], future: future.slice(1) },
        selectedLayerId:
          state.selectedLayerId && restoredLayerIds.has(state.selectedLayerId) ? state.selectedLayerId : null,
        selectedClipId:
          state.selectedClipId && restoredClipIds.has(state.selectedClipId) ? state.selectedClipId : null,
      });
      resetHistoryTracking(next);
    } finally {
      isTimeTraveling = false;
    }
  },

  addClip(media) {
    const state = get();
    if (state.trackSettings.video.locked) return;
    const clip = makeClip(media, 0);
    const wasEmpty = state.clips.length === 0;
    const activeAtPlayhead = selectActiveClip(state, state.currentTime);
    const activeIndex = activeAtPlayhead
      ? state.clips.findIndex((item) => item.id === activeAtPlayhead.id)
      : -1;
    const activeRenderStart = activeIndex >= 0
      ? renderStartTimes(state.clips)[activeIndex] ?? activeAtPlayhead!.start
      : state.currentTime;
    const contentPlayhead = activeAtPlayhead
      ? activeAtPlayhead.start + Math.max(0, state.currentTime - activeRenderStart)
      : state.currentTime;
    const edit = addClipByTimelineMode(
      state.clips,
      clip,
      contentPlayhead,
      state.timelineInsertMode,
      makeId
    );
    const nextLayers = edit.rippleDelta > 0
      ? state.layers.map((layer) => layer.start >= edit.insertAt
        ? { ...layer, start: layer.start + edit.rippleDelta, end: layer.end + edit.rippleDelta }
        : layer.end > edit.insertAt
          ? { ...layer, end: layer.end + edit.rippleDelta }
          : layer)
      : state.layers;
    const nextAudioTracks = edit.rippleDelta > 0
      ? state.audioTracks.map((track) => {
          const start = track.start ?? 0;
          const length = track.timelineDuration ?? track.duration ?? 0;
          if (start >= edit.insertAt) return { ...track, start: start + edit.rippleDelta };
          if (start + length > edit.insertAt) return { ...track, timelineDuration: length + edit.rippleDelta };
          return track;
        })
      : state.audioTracks;
    const selectedAudio = nextAudioTracks.find((track) => track.id === state.selectedAudioTrackId) ?? null;
    set({
      clips: edit.clips,
      layers: nextLayers,
      audioTracks: nextAudioTracks,
      audioTrack: selectedAudio,
      selectedClipId: clip.id,
      currentTime: edit.insertAt,
      isPlaying: false,
    });
    if (wasEmpty && !state.aspectTouched) {
      void get().autoMatchAspectFromClip(clip.src);
    }
  },

  removeClip(clipId) {
    const state = get();
    if (state.trackSettings.video.locked) return;
    const index = state.clips.findIndex((c) => c.id === clipId);
    if (index < 0) return;
    const nextClips = recalcClipStarts(state.clips.filter((c) => c.id !== clipId));
    const nextSelected = nextClips[Math.min(index, nextClips.length - 1)] ?? null;
    set({
      clips: nextClips,
      selectedClipId: nextSelected?.id ?? null,
      currentTime: nextSelected
        ? renderStartTimes(nextClips)[nextClips.findIndex((clip) => clip.id === nextSelected.id)] ?? nextSelected.start
        : 0,
      isPlaying: false,
    });
  },

  trimClipIn(clipId, deltaSeconds) {
    if (get().trackSettings.video.locked) return;
    set((state) => {
      const next = state.clips.map((c) => {
        if (c.id !== clipId) return c;
        const newOffset = snapTimeToFrame(Math.max(0, c.mediaOffset + deltaSeconds));
        const consumed = newOffset - c.mediaOffset;
        const newDuration = snapTimeToFrame(Math.max(0.5, c.duration - consumed));
        return { ...c, mediaOffset: newOffset, duration: newDuration, isTrimmed: true };
      });
      return { clips: recalcClipStarts(next) };
    });
  },

  trimClipOut(clipId, deltaSeconds) {
    if (get().trackSettings.video.locked) return;
    set((state) => {
      const next = state.clips.map((c) =>
        c.id === clipId
          ? { ...c, duration: snapTimeToFrame(Math.max(0.5, c.duration + deltaSeconds)), isTrimmed: true }
          : c
      );
      return { clips: recalcClipStarts(next) };
    });
  },

  splitClipAtPlayhead() {
    const state = get();
    if (state.trackSettings.video.locked) return;
    const active = selectActiveClip(state, state.currentTime);
    if (!active) return;
    const index = state.clips.findIndex((clip) => clip.id === active.id);
    const renderStart = renderStartTimes(state.clips)[index] ?? active.start;
    const localTime = Math.max(0, state.currentTime - renderStart);
    set({ clips: splitClipAt(state.clips, active.id, active.start + localTime, makeId) });
  },

  reorderClips(fromIndex, toIndex) {
    if (get().trackSettings.video.locked) return;
    set((state) => {
      if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= state.clips.length) return state;
      const clips = [...state.clips];
      const [moved] = clips.splice(fromIndex, 1);
      const clampedTo = Math.max(0, Math.min(toIndex, clips.length));
      clips.splice(clampedTo, 0, moved);
      return { clips: recalcClipStarts(clips) };
    });
  },

  duplicateClip(clipId) {
    const state = get();
    if (state.trackSettings.video.locked) return;
    const index = state.clips.findIndex((c) => c.id === clipId);
    if (index < 0) return;
    const clip = state.clips[index];
    const duplicate: EditorTimelineClip = {
      ...clip,
      id: makeId('clip'),
      start: clip.start + safeClipDuration(clip.duration),
      isTrimmed: Boolean(clip.isTrimmed),
    };
    const nextClips = recalcClipStarts([
      ...state.clips.slice(0, index + 1),
      duplicate,
      ...state.clips.slice(index + 1),
    ]);
    const nextSelected = nextClips[index + 1] ?? duplicate;
    set({
      clips: nextClips,
      selectedClipId: nextSelected.id,
      currentTime: renderStartTimes(nextClips)[index + 1] ?? nextSelected.start,
      isPlaying: false,
    });
  },

  updateClip(clipId, patch) {
    if (get().trackSettings.video.locked) return;
    set((state) => {
      const clips = state.clips.map((clip) => {
        if (clip.id !== clipId) return clip;
        const previousRate = clipPlaybackRate(clip);
        const requestedRate = patch.playbackRate === undefined
          ? previousRate
          : clipPlaybackRate({ playbackRate: patch.playbackRate });
        const patchedDuration = patch.duration === undefined
          ? clip.duration
          : Math.max(0.5, Number.isFinite(patch.duration) ? patch.duration : clip.duration);
        const duration = patch.playbackRate === undefined || patch.duration !== undefined
          ? patchedDuration
          : Math.max(0.5, (clip.duration * previousRate) / requestedRate);
        const next = { ...clip, ...patch, duration, playbackRate: requestedRate };
        return {
          ...next,
          fit: clipFit(next),
          positionX: clipPositionX(next),
          positionY: clipPositionY(next),
          scale: clipScale(next),
          rotation: clipRotation(next),
          opacity: clipOpacity(next),
          volume:
            patch.volume === undefined
              ? clip.volume ?? 1
              : Math.min(1, Math.max(0, Number.isFinite(patch.volume) ? patch.volume : 1)),
        };
      });
      return { clips: recalcClipStarts(clips) };
    });
  },

  selectClip(clipId) {
    if (clipId === null) {
      set({ selectedClipId: null });
      return;
    }
    const clip = get().clips.find((c) => c.id === clipId);
    if (!clip) return;
    const index = get().clips.findIndex((item) => item.id === clip.id);
    const renderStart = renderStartTimes(get().clips)[index] ?? clip.start;
    set({ selectedClipId: clip.id, selectedLayerId: null, currentTime: renderStart, isPlaying: false });
  },

  setTransition(clipId, transition) {
    if (!TRANSITION_TYPES.includes(transition.type)) return;
    set((state) => {
      const index = state.clips.findIndex((c) => c.id === clipId);
      // Not found, or the first clip — nothing precedes it to crossfade from.
      if (index <= 0) return state;
      return {
        clips: state.clips.map((c, i) =>
          i === index
            ? { ...c, transitionIn: { type: transition.type, durationSec: clampTransitionDuration(transition.durationSec) } }
            : c
        ),
      };
    });
  },

  removeTransition(clipId) {
    set((state) => ({
      clips: state.clips.map((c) => (c.id === clipId ? { ...c, transitionIn: undefined } : c)),
    }));
  },

  addTextLayer() {
    const state = get();
    if (state.trackSettings.overlays.locked) return;
    const range = defaultLayerRange(state);
    const layer: EditorOverlayLayer = {
      id: makeId('text'),
      type: 'text',
      label: 'Текст',
      text: 'Ваш текст',
      x: 50,
      y: 28,
      size: 96,
      color: '#ffffff',
      textColor: '#ffffff',
      backgroundColor: 'transparent',
      start: range.start,
      end: range.end,
      opacity: 1,
      fontFamily: 'montserrat',
      fontWeight: '800',
      fontStyle: 'normal',
      underline: false,
    };
    set({ layers: [...state.layers, layer], selectedLayerId: layer.id });
    get().setActiveTool('text');
  },
  // NOTE: text `size` is authored in render-canvas px (1080×1920 etc.); the
  // preview scales it to match. ~96px ≈ a readable 5% of a 9:16 frame height.

  addCaptionLayer() {
    // A caption is just a `type: 'text'` layer tagged `role: 'caption'` —
    // the existing preview render and server render-worker already draw
    // any text layer, so no render changes are needed. Bottom-center
    // position, bold white text (the existing text-layer drop-shadow
    // keeps it readable over video), short ~2.5s default duration from
    // the playhead.
    const state = get();
    if (state.trackSettings.overlays.locked) return;
    const range = defaultLayerRange(state, 2.5);
    const layer: EditorOverlayLayer = {
      id: makeId('caption'),
      type: 'text',
      role: 'caption',
      label: 'Субтитр',
      text: 'Субтитр',
      x: 50,
      y: 88,
      size: 60,
      color: '#ffffff',
      textColor: '#ffffff',
      backgroundColor: 'transparent',
      start: range.start,
      end: range.end,
      opacity: 1,
      fontFamily: 'montserrat',
      fontWeight: '700',
      fontStyle: 'normal',
      underline: false,
    };
    set({ layers: [...state.layers, layer], selectedLayerId: layer.id });
    get().setActiveTool('captions');
  },

  addPresetLayer(preset) {
    const state = get();
    if (state.trackSettings.overlays.locked) return;
    const range = defaultLayerRange(state);
    const layer: EditorOverlayLayer = {
      id: makeId(preset.layerType === 'sticker' ? 'sticker' : 'text'),
      type: preset.layerType,
      label: preset.layerType === 'sticker' ? 'Стикер' : 'Текст',
      text: preset.defaultText ?? (preset.layerType === 'sticker' ? preset.name : 'Ваш текст'),
      x: 50,
      y: 28,
      size: preset.size,
      color: preset.color,
      textColor: preset.color,
      backgroundColor: preset.backgroundColor ?? 'transparent',
      start: range.start,
      end: range.end,
      opacity: 1,
      fontFamily: preset.fontFamily,
      fontWeight: preset.fontWeight,
      fontStyle: preset.fontStyle,
      underline: preset.underline ?? false,
      // Re-applies the style subset via the same helper used for
      // apply-to-existing-layer, so the two code paths can't drift.
      ...presetToLayerPatch(preset),
    };
    set({ layers: [...state.layers, layer], selectedLayerId: layer.id });
    get().setActiveTool('text');
  },

  applyOverlayTemplate(template) {
    const state = get();
    if (state.trackSettings.overlays.locked) return;
    const duration = selectDuration(state);
    const insertAt = clampTime(state.currentTime, duration);
    const templateDuration = Math.max(
      0.1,
      ...template.layers.map((layer) => layer.offsetStart + layer.duration)
    );
    const sharedEnd = Math.max(
      insertAt + 0.1,
      clampTime(insertAt + templateDuration, duration)
    );
    const matchedLayerIds = new Set<string>();
    const matchingLayers = template.layers.map((templateLayer) => {
      const legacyStart = insertAt + templateLayer.offsetStart;
      const match = state.layers.find(
        (layer) =>
          !matchedLayerIds.has(layer.id) &&
          layer.text === templateLayer.text &&
          Math.abs(layer.end - sharedEnd) <= 0.08 &&
          (Math.abs(layer.start - insertAt) <= 0.08 || Math.abs(layer.start - legacyStart) <= 0.08)
      );
      if (match) matchedLayerIds.add(match.id);
      return match;
    });

    // Re-applying the same template at the same playhead upgrades legacy
    // staggered instances in place instead of stacking a duplicate copy.
    if (matchingLayers.every(Boolean) && matchingLayers.length === template.layers.length) {
      set({
        layers: state.layers.map((layer) =>
          matchedLayerIds.has(layer.id) ? { ...layer, start: insertAt, end: sharedEnd } : layer
        ),
        selectedLayerId: matchingLayers[0]?.id ?? state.selectedLayerId,
        selectedClipId: null,
        currentTime: Math.min(duration, insertAt + 0.01),
        isPlaying: false,
      });
      get().setActiveTool('templates');
      return;
    }
    const newLayers: EditorOverlayLayer[] = template.layers.map((tl) => {
      return {
        id: makeId('text'),
        type: tl.type ?? 'text',
        label: tl.text,
        text: tl.text,
        x: tl.x,
        y: tl.y,
        size: tl.size,
        color: tl.color,
        textColor: tl.color,
        backgroundColor: tl.backgroundColor ?? 'transparent',
        badgeStyle: tl.badgeStyle,
        badgeAccentColor: tl.badgeAccentColor,
        badgeBorderColor: tl.badgeBorderColor,
        badgeBorderWidth: tl.badgeBorderWidth,
        rotation: tl.rotation,
        start: insertAt,
        end: sharedEnd,
        opacity: tl.opacity ?? 1,
        fontFamily: tl.fontFamily,
        fontWeight: tl.fontWeight,
        fontStyle: tl.fontStyle,
        underline: tl.underline ?? false,
      };
    });
    const revealAt = Math.min(duration, insertAt + 0.01);
    set({
      layers: [...state.layers, ...newLayers],
      selectedLayerId: newLayers[0]?.id ?? state.selectedLayerId,
      selectedClipId: null,
      currentTime: revealAt,
      isPlaying: false,
    });
    get().setActiveTool('templates');
  },

  addStickerLayer(sticker) {
    const state = get();
    if (state.trackSettings.overlays.locked) return;
    const range = defaultLayerRange(state);
    const layer: EditorOverlayLayer = {
      id: makeId('sticker'),
      type: 'sticker',
      label: 'Стикер',
      text: sticker.title,
      x: 50,
      y: 50,
      size: 84,
      color: '#ffffff',
      textColor: '#ffffff',
      backgroundColor: 'transparent',
      start: range.start,
      end: range.end,
      opacity: 1,
      fontFamily: 'montserrat',
      fontWeight: '700',
      fontStyle: 'normal',
      underline: false,
      src: sticker.src,
      s3Key: sticker.s3Key,
      mimeType: sticker.mimeType,
      widthPercent: 26,
    };
    set({ layers: [...state.layers, layer], selectedLayerId: layer.id });
    get().setActiveTool('stickers');
  },

  addAsset(asset) {
    set((state) => ({
      assets: state.assets.some((item) => item.id === asset.id)
        ? state.assets
        : [asset, ...state.assets],
    }));
  },

  addEffectLayer(effectSrc, label) {
    const state = get();
    if (state.trackSettings.overlays.locked) return;
    const range = defaultLayerRange(state);
    const effect = getEffectPreset(effectSrc, label);
    const layer: EditorOverlayLayer = {
      id: makeId('effect'),
      type: 'effect',
      label,
      text: label,
      x: 50,
      y: 50,
      size: 40,
      color: effect.defaultColor,
      textColor: '#ffffff',
      backgroundColor: 'transparent',
      start: range.start,
      end: range.end,
      opacity: effect.defaultOpacity,
      fontFamily: 'montserrat',
      fontWeight: '700',
      fontStyle: 'normal',
      underline: false,
      src: effect.id,
    };
    set({ layers: [...state.layers, layer], selectedLayerId: layer.id });
    get().setActiveTool('effects');
  },

  updateLayer(layerId, patch) {
    if (get().trackSettings.overlays.locked) return;
    set((state) => ({
      layers: state.layers.map((layer) => {
        if (layer.id !== layerId) return layer;
        const animatableKeys = ['x', 'y', 'size', 'widthPercent', 'rotation', 'opacity'] as const;
        const frames = cleanLayerKeyframes(layer.keyframes, layer);
        const hasKeyframes = frames.length > 0;
        const currentKeyframe = frames.find(
          (keyframe) => Math.abs(keyframe.time - state.currentTime) <= frameDuration() / 2
        );
        const keyframePatch = Object.fromEntries(
          animatableKeys
            .filter((key) => patch[key] !== undefined)
            .map((key) => [key, patch[key]])
        ) as Partial<EditorOverlayLayer>;
        const hasAnimatablePatch = Object.keys(keyframePatch).length > 0;
        // Once a layer is keyframed, `layerVisualAt` reads ONLY interpolated
        // keyframe values and never the base fields — so animatable edits must
        // land on a keyframe, never on base, or they silently no-op off-frame.
        const routeAnimatableToKeyframe = hasKeyframes && hasAnimatablePatch;
        const basePatch =
          currentKeyframe || routeAnimatableToKeyframe
            ? Object.fromEntries(
                Object.entries(patch).filter(
                  ([key]) => !animatableKeys.includes(key as typeof animatableKeys[number])
                )
              )
            : patch;
        const next = { ...layer, ...basePatch };
        if (hasAnimatablePatch && currentKeyframe) {
          // Playhead on an existing keyframe → update it in place.
          next.keyframes = cleanLayerKeyframes(
            layer.keyframes?.map((keyframe) =>
              keyframe.id === currentKeyframe.id ? { ...keyframe, ...keyframePatch } : keyframe
            ),
            next
          );
        } else if (hasAnimatablePatch && hasKeyframes) {
          // Playhead off any keyframe on a keyframed layer → put the edit on a
          // keyframe at the playhead (seeded from the interpolated value) so the
          // drag/resize/rotate/slider edit actually takes effect.
          const time = snapTimeToFrame(
            Math.min(layer.end, Math.max(layer.start, state.currentTime))
          );
          const seed = layerVisualSnapshot(layerVisualAt(layer, time));
          // If the resolved time collapses onto an existing keyframe's frame
          // (e.g. currentTime outside the window clamps to start/end), update it
          // IN PLACE instead of minting a new id that would silently replace it.
          const atTime = frames.find(
            (keyframe) => Math.abs(keyframe.time - time) <= frameDuration() / 2
          );
          next.keyframes = cleanLayerKeyframes(
            atTime
              ? (layer.keyframes ?? []).map((keyframe) =>
                  keyframe.id === atTime.id ? { ...keyframe, time, ...seed, ...keyframePatch } : keyframe
                )
              : [...(layer.keyframes ?? []), { id: makeId('keyframe'), time, ...seed, ...keyframePatch }],
            next
          );
        }
        if (patch.start !== undefined) next.start = snapTimeToFrame(Math.max(0, patch.start));
        if (patch.end !== undefined) next.end = snapTimeToFrame(Math.max(next.start + 0.2, patch.end));
        if (patch.start !== undefined || patch.end !== undefined) {
          next.keyframes = cleanLayerKeyframes(next.keyframes, next);
        }
        return next;
      }),
    }));
  },

  addLayerKeyframe(layerId, requestedTime) {
    if (get().trackSettings.overlays.locked) return;
    set((state) => ({
      layers: state.layers.map((layer) => {
        if (layer.id !== layerId) return layer;
        const time = snapTimeToFrame(
          Math.min(layer.end, Math.max(layer.start, requestedTime ?? state.currentTime))
        );
        const existing = cleanLayerKeyframes(layer.keyframes, layer);
        const visual = layerVisualSnapshot(layerVisualAt(layer, time));
        const atTime = existing.find((keyframe) => Math.abs(keyframe.time - time) <= frameDuration() / 2);
        const keyframes = atTime
          ? existing.map((keyframe) => keyframe.id === atTime.id ? { ...keyframe, time, ...visual } : keyframe)
          : [...existing, { id: makeId('keyframe'), time, ...visual }];
        return { ...layer, keyframes: cleanLayerKeyframes(keyframes, layer) };
      }),
    }));
  },

  removeLayerKeyframe(layerId, keyframeId) {
    if (get().trackSettings.overlays.locked) return;
    set((state) => ({
      layers: state.layers.map((layer) => {
        if (layer.id !== layerId) return layer;
        const keyframes = cleanLayerKeyframes(layer.keyframes, layer).filter(
          (keyframe) => keyframe.id !== keyframeId
        );
        return { ...layer, keyframes: keyframes.length ? keyframes : undefined };
      }),
    }));
  },

  removeLayer(layerId) {
    if (get().trackSettings.overlays.locked) return;
    set((state) => ({
      layers: state.layers.filter((layer) => layer.id !== layerId),
      selectedLayerId: state.selectedLayerId === layerId ? null : state.selectedLayerId,
    }));
  },

  selectLayer(layerId) {
    if (layerId === null) {
      set({ selectedLayerId: null });
      return;
    }
    const layer = get().layers.find((l) => l.id === layerId);
    set({ selectedLayerId: layerId, selectedClipId: null });
    if (layer) get().setActiveTool(getToolForLayer(layer));
  },

  setAudioTrack(track) {
    if (get().trackSettings.music.locked) return;
    if (!track) {
      const selectedId = get().selectedAudioTrackId;
      if (selectedId) get().removeAudioTrack(selectedId);
      else set({ audioTrack: null, audioTracks: [] });
      return;
    }
    const state = get();
    const id = track.id ?? state.selectedAudioTrackId;
    if (id && state.audioTracks.some((item) => item.id === id)) {
      get().updateAudioTrack(id, track);
      return;
    }
    get().addAudioTrack(track);
  },

  addAudioTrack(track) {
    if (get().trackSettings.music.locked) return;
    const state = get();
    const projectDuration = selectDuration(state);
    const start = snapTimeToFrame(Math.min(projectDuration, Math.max(0, track.start ?? state.currentTime)));
    const available = Math.max(0.1, projectDuration > 0 ? projectDuration - start : track.duration ?? 30);
    const timelineDuration = snapTimeToFrame(
      Math.max(0.1, Math.min(track.timelineDuration ?? track.duration ?? available, available))
    );
    const next: EditorAudioTrack = {
      ...track,
      id: makeId('audio'),
      start,
      timelineDuration,
      sourceOffset: Math.max(0, track.sourceOffset ?? 0),
    };
    set({
      audioTracks: [...state.audioTracks, next],
      audioTrack: next,
      selectedAudioTrackId: next.id ?? null,
      selectedClipId: null,
      selectedLayerId: null,
    });
    get().setActiveTool('audio');
  },

  updateAudioTrack(trackId, patch) {
    if (get().trackSettings.music.locked) return;
    set((state) => {
      const audioTracks = state.audioTracks.map((track) => {
        if (track.id !== trackId) return track;
        const next = { ...track, ...patch, id: track.id };
        if (patch.start !== undefined) next.start = snapTimeToFrame(Math.max(0, patch.start));
        if (patch.timelineDuration !== undefined) {
          next.timelineDuration = snapTimeToFrame(Math.max(0.1, patch.timelineDuration));
        }
        if (patch.sourceOffset !== undefined) next.sourceOffset = Math.max(0, patch.sourceOffset);
        return next;
      });
      const selected = audioTracks.find((track) => track.id === state.selectedAudioTrackId) ?? null;
      return { audioTracks, audioTrack: selected };
    });
  },

  removeAudioTrack(trackId) {
    if (get().trackSettings.music.locked) return;
    set((state) => {
      const audioTracks = state.audioTracks.filter((track) => track.id !== trackId);
      const selectedAudioTrackId = state.selectedAudioTrackId === trackId
        ? audioTracks[0]?.id ?? null
        : state.selectedAudioTrackId;
      return {
        audioTracks,
        selectedAudioTrackId,
        audioTrack: audioTracks.find((track) => track.id === selectedAudioTrackId) ?? null,
      };
    });
  },

  selectAudioTrack(trackId) {
    const track = get().audioTracks.find((item) => item.id === trackId) ?? null;
    set({
      selectedAudioTrackId: track?.id ?? null,
      audioTrack: track,
      selectedClipId: null,
      selectedLayerId: null,
    });
    if (track) get().setActiveTool('audio');
  },

  setTrackSettings(kind, patch) {
    set((state) => ({
      trackSettings: {
        ...state.trackSettings,
        [kind]: { ...state.trackSettings[kind], ...patch },
      },
    }));
  },

  setAspectRatio(ar, explicit) {
    set((state) => ({ aspectRatio: ar, aspectTouched: explicit ? true : state.aspectTouched }));
  },

  async autoMatchAspectFromClip(src) {
    if (get().aspectTouched) return;
    const nearest = await detectVideoAspectRatio(src);
    if (nearest && !get().aspectTouched) {
      get().setAspectRatio(nearest, true);
    }
  },

  setCrossfade(seconds) {
    set({ crossfadeDuration: seconds });
  },

  setFilter(id) {
    if (!VALID_FILTER_IDS.has(id)) return;
    set((state) => {
      if (!state.selectedClipId) return state;
      return {
        clips: state.clips.map((clip) =>
          clip.id === state.selectedClipId ? { ...clip, filterId: id } : clip
        ),
      };
    });
  },

  setModelId(id) {
    set({ modelId: id });
  },

  setLibrary(items) {
    set({ library: items });
  },

  setLeftMode(mode) {
    set({ leftMode: mode });
  },

  setActiveTool(tool) {
    set((state) => {
      const mapped = LEFT_MODE_BY_TOOL[tool];
      const leftMode = mapped ?? (state.leftMode === 'chat' ? 'media' : state.leftMode);
      return { activeTool: tool, leftMode };
    });
  },

  setTimelineInsertMode(mode) {
    if (mode !== 'append' && mode !== 'insert' && mode !== 'overwrite') return;
    set({ timelineInsertMode: mode });
  },

  setCurrentTime(t) {
    const duration = selectDuration(get());
    set({ currentTime: Math.min(duration, snapTimeToFrame(Math.max(0, t))) });
  },

  setIsPlaying(p) {
    set({ isPlaying: p });
  },

  toSavePayload() {
    const state = get();
    const clips = recalcClipStarts(persistableClips(state.clips));
    return {
      projectId: state.projectId,
      title: clips[0]?.title?.replace(/\.[^.]+$/, '') || 'Untitled edit',
      aspectRatio: state.aspectRatio,
      duration: clips.reduce((sum, c) => sum + safeClipDuration(c.duration), 0),
      clips,
      layers: persistableLayers(state.layers),
      assets: persistableAssets(state.assets),
      audioTrack: state.audioTracks[0] ?? undefined,
      audioTracks: state.audioTracks,
      trackSettings: state.trackSettings,
      crossfadeDuration: state.crossfadeDuration,
      filterId: 'none',
      modelId: state.modelId,
      genProposals: state.genProposals,
    };
  },

  setProjectId(id) {
    set({ projectId: id });
  },

  setSaving(b) {
    set({ saving: b });
  },

  setRenderJobId(id) {
    set({ renderJobId: id });
  },

  setLastBriefReferenceUrl(url) {
    set({ lastBriefReferenceUrl: url });
  },

  async createGenProposal(toolCallId, summary, shots, referenceUrl, authedFetch) {
    // Idempotent: a caller may re-invoke this for the same tool call (e.g.
    // a re-render of the chat message) — never quote/push twice.
    if (get().genProposals.some((p) => p.toolCallId === toolCallId)) return;

    const sortedShots = [...shots].sort((a, b) => a.order - b.order);
    const placeholderJobs: GenShotJob[] = sortedShots.map((shot, i) => ({
      localId: `${toolCallId}-${i}`,
      shot,
      state: 'quoting',
      modelLabel: wavespeedModelLabel(shotToWavespeedRequest(shot, referenceUrl).model),
    }));

    // Reserve immediately (synchronous, no `await` before this point) so a
    // concurrent call with the same toolCallId sees it and bails out above.
    set((state) => ({
      genProposals: [
        ...state.genProposals,
        {
          toolCallId,
          summary,
          jobs: placeholderJobs,
          referenceUrl,
          totalEstimateUsd: 0,
          balanceUsd: 0,
          decided: 'pending' as const,
        },
      ],
    }));

    // costUsd keyed by localId — never write the priced GenShotJob objects
    // themselves back into state, since by the time we get here another
    // action (allowGenProposal) may have already mutated those jobs (or the
    // whole proposal) out from under this stale snapshot.
    const costByLocalId = new Map<string, number>();
    let totalEstimateUsd = 0;
    for (const job of placeholderJobs) {
      let costUsd = 0;
      try {
        const { model, params } = shotToWavespeedRequest(job.shot, referenceUrl);
        const url = `/api/ai/wavespeed/quote?model=${encodeURIComponent(model)}&params=${encodeURIComponent(JSON.stringify(params))}`;
        const res = await authedFetch(url);
        const json = await res.json();
        if (json?.success && typeof json?.data?.price === 'number') {
          costUsd = json.data.price;
        }
      } catch {
        // Quote failed for this shot — leave costUsd at 0, estimate will be
        // a floor rather than blocking the whole proposal.
      }
      totalEstimateUsd += costUsd;
      costByLocalId.set(job.localId, costUsd);
    }

    let balanceUsd = 0;
    try {
      const res = await authedFetch('/api/auth/me');
      const json = await res.json();
      if (typeof json?.user?.tokens === 'number') balanceUsd = json.user.tokens;
    } catch {
      // balance stays 0 — the allow/decline UI will show an unaffordable
      // proposal rather than silently letting it through.
    }

    // Functional update reading the CURRENT state (not the stale snapshot
    // above): bail entirely if the proposal is gone or already decided, and
    // patch only jobs still `quoting` — matched by localId, never a
    // wholesale array replace. If `allowGenProposal` ran during the awaits
    // above, it already refused to submit anything still `quoting` (see
    // below), so any job this proposal has moved to `submitting`/`running`/
    // `error` is left completely untouched here.
    set((state) => {
      const proposal = state.genProposals.find((p) => p.toolCallId === toolCallId);
      if (!proposal || proposal.decided !== 'pending') return state;
      return {
        genProposals: state.genProposals.map((p) =>
          p.toolCallId === toolCallId
            ? {
                ...p,
                totalEstimateUsd,
                balanceUsd,
                jobs: p.jobs.map((j) =>
                  j.state === 'quoting' && costByLocalId.has(j.localId)
                    ? { ...j, state: 'awaiting' as const, costUsd: costByLocalId.get(j.localId) }
                    : j
                ),
              }
            : p
        ),
      };
    });
  },

  allowGenProposal(toolCallId, authedFetch) {
    const proposal = get().genProposals.find((p) => p.toolCallId === toolCallId);
    if (!proposal || proposal.decided !== 'pending') return;
    if (proposal.jobs.some((j) => j.state === 'quoting')) return;

    set((state) => ({
      genProposals: state.genProposals.map((p) =>
        p.toolCallId === toolCallId ? { ...p, decided: 'allowed' as const } : p
      ),
    }));

    void (async () => {
      for (const job of proposal.jobs) {
        await submitGenerationShot(job, proposal, authedFetch, (patch) => {
          get().updateGenJob(toolCallId, job.localId, patch);
        });
      }
    })();
  },

  retryGenJob(toolCallId, localId, authedFetch) {
    const proposal = get().genProposals.find((item) => item.toolCallId === toolCallId);
    const job = proposal?.jobs.find((item) => item.localId === localId);
    // A job with a provider id may already have been charged. Its lifecycle
    // must be resolved by polling/refund, not by silently creating a second
    // paid task. Retry here is only for pre-submit failures such as Unknown model.
    if (!proposal || !job || job.state !== 'error' || job.jobId) return;
    void submitGenerationShot(job, proposal, authedFetch, (patch) => {
      get().updateGenJob(toolCallId, localId, patch);
    });
  },

  declineGenProposal(toolCallId) {
    const proposal = get().genProposals.find((p) => p.toolCallId === toolCallId);
    if (!proposal || proposal.decided !== 'pending') return;

    set((state) => ({
      genProposals: state.genProposals.map((p) =>
        p.toolCallId === toolCallId
          ? {
              ...p,
              decided: 'declined' as const,
              jobs: p.jobs.map((j) => ({ ...j, state: 'declined' as const })),
            }
          : p
      ),
    }));
  },

  updateGenJob(toolCallId, localId, patch) {
    set((state) => ({
      genProposals: state.genProposals.map((p) =>
        p.toolCallId === toolCallId
          ? { ...p, jobs: p.jobs.map((j) => (j.localId === localId ? { ...j, ...patch } : j)) }
          : p
      ),
    }));
  },
}));

function docSnapshotsEqual(a: DocSnapshot, b: DocSnapshot): boolean {
  return (
    a.clips === b.clips &&
    a.layers === b.layers &&
    a.assets === b.assets &&
    a.audioTrack === b.audioTrack &&
    a.audioTracks === b.audioTracks &&
    a.aspectRatio === b.aspectRatio &&
    a.crossfadeDuration === b.crossfadeDuration &&
    a.filterId === b.filterId
    && a.trackSettings === b.trackSettings
  );
}

/**
 * Fires `HISTORY_COALESCE_MS` after the last doc change in a burst (see the
 * `subscribe` listener below). Commits the pre-burst snapshot
 * (`lastCommittedDoc`) into `history.past`, re-baselines `lastCommittedDoc`
 * to the now-current doc, and clears `history.future` — a fresh edit always
 * invalidates redo. Exported for tests that want to bypass the real timer
 * (fake timers + `vi.advanceTimersByTime` also work, since this is exactly
 * what the debounce eventually calls).
 */
export function commitHistory() {
  // Guards direct calls (e.g. from tests) racing a still-pending timer, not
  // just the timer's own callback invocation (where this is a no-op).
  if (historyDebounceTimer) {
    clearTimeout(historyDebounceTimer);
    historyDebounceTimer = null;
  }
  const state = useEditorStore.getState();
  const current = snapshotDoc(state);
  if (docSnapshotsEqual(current, lastCommittedDoc)) return;
  const past = [...state.history.past, lastCommittedDoc].slice(-HISTORY_LIMIT);
  lastCommittedDoc = current;
  useEditorStore.setState({ history: { past, future: [] } });
}

// Coalescing capture: any doc-slice change (by reference — every mutating
// action above always replaces the slice it touches) not caused by undo/redo
// itself (re-)arms a debounce timer. Rapid consecutive edits (e.g. a slider
// drag) keep resetting the timer, so only ONE `commitHistory` fires once
// things go quiet — coalescing the whole burst into a single undo step.
useEditorStore.subscribe((state, prevState) => {
  if (isTimeTraveling) return;
  if (docSnapshotsEqual(snapshotDoc(state), snapshotDoc(prevState))) return;
  if (historyDebounceTimer) clearTimeout(historyDebounceTimer);
  historyDebounceTimer = setTimeout(commitHistory, HISTORY_COALESCE_MS);
});

export function selectCanUndo(state: EditorState): boolean {
  return state.history.past.length > 0;
}

export function selectCanRedo(state: EditorState): boolean {
  return state.history.future.length > 0;
}

export function selectDuration(state: EditorState): number {
  return effectiveTimelineDuration(state.clips);
}

export function selectActiveClip(state: EditorState, currentTime: number): EditorTimelineClip | null {
  if (state.clips.length === 0) return null;
  const duration = selectDuration(state);
  if (duration > 0 && currentTime >= duration - 0.02) {
    return state.clips[state.clips.length - 1];
  }
  const starts = renderStartTimes(state.clips);
  for (let index = state.clips.length - 1; index >= 1; index -= 1) {
    const clip = state.clips[index];
    const incoming = effectiveTransitionDuration(
      state.clips[index - 1].duration,
      clip.duration,
      clip.transitionIn
    );
    if (currentTime >= starts[index] + incoming - 0.001) return clip;
  }
  return state.clips[0];
}

export function selectSelectedClip(state: EditorState): EditorTimelineClip | null {
  return state.clips.find((clip) => clip.id === state.selectedClipId) ?? selectActiveClip(state, state.currentTime);
}

export function selectSelectedLayer(state: EditorState): EditorOverlayLayer | null {
  return state.layers.find((layer) => layer.id === state.selectedLayerId) ?? null;
}
